import React from 'react'
import moment from 'moment'

import uniq from 'lodash/uniq'

import Box from '@eig-builder/core-ui/Box'

import CHART_TYPES from '../../charts'
import NpsFilterOptions from '../../helpers/nps-filter-options'

// import Partners from '../../../../crm/enums/partner-enum'

const genericCustomDataHandler = (data, dates, valueKey, keyString = 'type') => {
  const uniqueTypes = uniq(data
    .map(d => d.data)
    .map(d => d.find(o => typeof o.key === 'string' && o.key.toLowerCase() === keyString)?.value)
    .filter(Boolean))

  const formatted = []
  dates.forEach(label => {
    // Filter and group data by date (label)
    const dataFilteredForDate = data
      .filter(d => d.date === label)
      .map(d => {
        const filteredData = d.data
          .filter(d => typeof d.key === 'string' && (typeof d.value === 'string' || !isNaN(parseInt(d.value))))

        return {
          key: filteredData.find(o => o.key.toLowerCase() === keyString)?.value,
          value: filteredData.find(o => o.key.toLowerCase() === valueKey)?.value
        }
      })

    // If a certain type is missing for a date, we have to manually fill it with value 0
    const dataForLabel = []
    uniqueTypes.forEach(type => {
      const value = dataFilteredForDate.find(d => d.key === type)?.value || 0
      dataForLabel.push({
        key: type,
        value
      })
    })

    formatted.push({
      data: dataForLabel,
      date: label,
      label: ''
    })
  })
  return formatted
}

const tierLevelTableDataHandler = data => {
  if (data && data[0]) {
    const formatted = Object.entries(data[0]).map(([key, val]) => {
      return { tier: key, users: val }
    })

    return formatted
  }

  return data
}

export default {
  overviewWpCe: {
    charts: [
      {
        type: CHART_TYPES.HEADER,
        title: 'User'
      },
      {
        type: CHART_TYPES.CARD,
        name: 'totalemailaccountscreated',
        title: 'Total Users Created',
        tooltip: 'The total number of Creative Mail customers where a CTCT-account was created',
        fixedPartnerId: 60
      },
      {
        type: CHART_TYPES.CARD,
        title: 'Free Users',
        name: 'wordpresscreativemailtotalfreesignups',
        tooltip: [
          'The total number of free Creative Mail customers where a CTCT-account was created.',
          'This includes free users with Suspended/Cancelled services.'
        ]
      },
      {
        type: CHART_TYPES.CARD,
        title: 'Current Paid Users',
        name: 'WordpressCreativeMailCurrentPaidSignups',
        tooltip: 'The total number of Creative Mail customers with a linked CTCT-account that currently have a paid service'
      },
      {
        type: CHART_TYPES.CARD,
        title: 'Total Paid Users',
        name: 'wordpresscreativemailtotalpaidsignups',
        tooltip: 'The total number of Creative Mail customers with a linked CTCT-account that currently have a paid service, or had paid services in the past'
      },
      {
        type: CHART_TYPES.CARD,
        title: 'Users with Cancelled Services',
        name: 'WordpressCreativeMailTotalAccountsWithServiceCancelled',
        tooltip: 'The total number of Creative Mail customers with a linked CTCT-account that only have cancelled services'
      },
      {
        type: CHART_TYPES.CARD,
        title: 'Users with Suspended Services',
        name: 'WordpressCreativeMailTotalAccountsWithServiceSuspended',
        tooltip: 'he total number of Creative Mail customers with a linked CTCT-account that only have suspended services'
      },
      {
        type: CHART_TYPES.CARD,
        title: 'Free to Paid conversion',
        name: 'WordpressCreativeMailFreeToPaidConversion',
        tooltip: 'Percentage of Total Paid Users (including users that were paid, but are not anymore) relative to the Total number of Creative Mail users',
        suffix: '%'
      },
      {
        type: CHART_TYPES.CARD,
        name: 'totalcontactscreated',
        title: 'Total Contacts Subscribed',
        tooltip: 'The total number of contacts subscribed by Creative Mail customers',
        fixedPartnerId: 60
      },
      {
        type: CHART_TYPES.CARD,
        name: 'totalsuspendedaccounts',
        title: 'Total Suspended Users',
        tooltip: 'The total number of Creative Mail customers where the CTCT-account was suspended',
        fixedPartnerId: 60
      },
      {
        type: CHART_TYPES.CARD,
        name: 'totalsuspendedaccountsrate',
        title: 'Percentage Suspended Users',
        tooltip: 'The percentage of suspended accounts as part of the total number of CTCT-accounts for Creative Mail customers',
        fixedPartnerId: 60,
        suffix: '%'
      },
      {
        type: CHART_TYPES.CARD,
        name: 'wordpressaccountcreationnotfinishedtotal',
        title: 'Account creation not finished',
        tooltip: 'All Creative Mail accounts where no CTCT-account was created',
        fixedPartnerId: 60
      },
      {
        type: CHART_TYPES.PIE,
        title: 'Tier levels Free Users',
        name: 'FreePricingTierLevels',
        tooltip: 'Showing the number of free customers that are in a specific tier level range (used for billing)',
        forceNewRow: true,
        fixedPartnerId: 60
      },
      {
        type: CHART_TYPES.PIE,
        title: 'Tier levels Paid Users',
        name: 'PricingTierLevels',
        tooltip: 'Showing the number of paid customers that are in a specific tier level range (used for billing)',
        fixedPartnerId: 60
      },
      {
        type: CHART_TYPES.PIE,
        title: 'Plan distribution',
        name: 'wordpressplansdonut',
        tooltip: 'Distribution of the paid plans for Creative Mail',
        fixedPartnerId: 60
      },
      {
        className: 'grid-col-6',
        type: CHART_TYPES.TABLE,
        defaultRows: 20,
        condensed: true,
        title: 'Tier levels Free Users',
        name: 'freepricingtierlevels',
        tooltip: 'Showing the number of free customers that are in a specific tier level range (used for billing)',
        fixedPartnerId: 60,
        customDataHandler: tierLevelTableDataHandler,
        headers: [
          {
            name: 'tier',
            title: 'Tier'
          },
          {
            name: 'users',
            title: 'Users',
            alignRight: true
          }
        ]
      },
      {
        className: 'grid-col-6',
        type: CHART_TYPES.TABLE,
        defaultRows: 20,
        condensed: true,
        title: 'Tier levels Paid Users',
        name: 'pricingtierlevels',
        tooltip: 'Showing the number of paid customers that are in a specific tier level range (used for billing)',
        fixedPartnerId: 60,
        customDataHandler: tierLevelTableDataHandler,
        headers: [
          {
            name: 'tier',
            title: 'Tier'
          },
          {
            name: 'users',
            title: 'Users',
            alignRight: true
          }
        ]
      },
      {
        type: CHART_TYPES.HEADER,
        title: 'Campaign'
      },
      {
        type: CHART_TYPES.CARD,
        name: 'totalaccountswithcampaigns',
        title: 'Total Users with Campaigns',
        tooltip: 'The total number of Creative Mail customers where at least one campaign was created',
        fixedPartnerId: 60
      },
      {
        type: CHART_TYPES.CARD,
        name: 'totalaccountswithcampaignssend',
        title: 'Total Users with Campaigns Sent',
        tooltip: 'The total number of Creative Mail customers where at least one campaign was sent',
        fixedPartnerId: 60
      },
      {
        type: CHART_TYPES.CARD,
        name: 'totalcampaignscreated',
        title: 'Total Campaigns Created',
        tooltip: 'The total number of campaigns created by Creative Mail customers',
        fixedPartnerId: 60
      },
      {
        type: CHART_TYPES.CARD,
        name: 'totalcampaignssend',
        title: 'Total Campaigns Sent',
        tooltip: 'The total number of campaigns sent by Creative Mail customers',
        fixedPartnerId: 60
      },
      {
        type: CHART_TYPES.HEADER,
        title: 'WooCommerce'
      },
      {
        type: CHART_TYPES.CARD,
        name: 'TotalAccountsWithTransactionalEmailTemplates',
        title: 'Total Users with Woo Emails',
        tooltip: 'The total number of Creative Mail customers that have at least 1 WooCommerce template edited',
        fixedPartnerId: 60
      },
      {
        type: CHART_TYPES.CARD,
        name: 'TotalAccountsWithTransactionalEmailSends',
        title: 'Total Users with Woo Emails Sent',
        tooltip: 'The total number of Creative Mail customers that have at least 1 WooCommerce email sent',
        fixedPartnerId: 60
      },
      {
        type: CHART_TYPES.HEADER,
        title: 'Automation'
      },
      {
        type: CHART_TYPES.CARD,
        title: 'Total Users with Automation flows created',
        name: 'TotalAccountsWithWorkflows',
        tooltip: 'Shows the total number of users that created an Automation'
      },
      {
        type: CHART_TYPES.CARD,
        title: 'Total Users with Automation flows activated',
        name: 'TotalAccountsWithActiveWorkflows',
        tooltip: 'Shows the total number of users with an activated Automation'
      },
      {
        type: CHART_TYPES.CARD,
        title: 'Total Activated Automation flows',
        name: 'TotalWorkflowsActive',
        tooltip: 'Shows the total number of active Automations'
      }
    ],
    route: '/financial-reports/wordpress-cm/overview',
    hideDatePicker: true
  },
  salesWpCe: {
    charts: [
      {
        type: CHART_TYPES.GRAPH,
        title: 'Free to Paid Conversion',
        name: 'wordpresscreativemailfreepaidconversion',
        className: 'grid-col-12',
        doubleYAxis: true,
        suffix: '%',
        yAxis: ['Signups', 'Conversion'],
        tooltip: 'Signups are all Creative Mail accounts that have a CtCt-account connected. Conversion rates have a discrepancy because it’s possible that a customer joined on a date before the selected date period and bought a plan inside the selected date period.'
      },
      {
        type: CHART_TYPES.GRAPH,
        title: 'Newly bought plans',
        name: 'wordpressplansales',
        className: 'grid-col-12',
        tooltip: 'Newly bought plans for Creative plans in the selected period'
      },
      {
        type: CHART_TYPES.TABLE,
        title: 'Monthly ARPS',
        name: 'MonthlyArps',
        tooltip: 'Shows the total revenue in a month (refunds are subtracted) divided by the number of paid customers at the end of the month. Only the months selected in the date selector are shown.'
      },
      {
        type: CHART_TYPES.TABLE,
        title: 'Monthly Churn',
        name: 'WordpressMonthlyChurnRateTable'
      },
      {
        title: 'Transactions',
        type: CHART_TYPES.TABLE,
        name: 'TransactionsWithTracking',
        tooltip: 'An overview of all transactions in the specified date period'
      }
    ],
    route: '/financial-reports/wordpress-cm/sales',
    fixedPartnerId: 60,
    disableToday: true
  },
  satisfactionWpCe: {
    charts: [
      {
        type: CHART_TYPES.TABLE,
        title: 'Satisfaction',
        name: 'NpsEmail',
        tooltip: 'A table with the scores and number of responses for Creative Mail',
        className: 'grid-col-6'
      },
      {
        type: CHART_TYPES.GRAPH,
        title: 'Satisfaction trend',
        name: 'NpsEmailTrendsPerDay',
        tooltip: 'A graph with the satisfaction trend over time for Creative Mail'
      },
      {
        type: CHART_TYPES.CARD,
        title: 'Supporting comments',
        name: 'NpsEmailSupportingCommentsTotal',
        tooltip: 'The total number of supporting comments for Creative Mail in the specified period'
      },
      {
        type: CHART_TYPES.TABLE,
        title: 'Satisfaction comments',
        name: 'NpsEmailComments',
        tooltop: 'A table showing all supporting comments for Creative Mail in the specified period',
        enableCaching: false,
        isNpsTable: true,
        sorting: { default: { fieldNames: ['date'], direction: true } },
        filters: NpsFilterOptions,
        headers: [
          {
            type: 'nps-multi-select',
            disableRouteClick: true
          },
          {
            name: 'AccountId',
            title: 'Account Id',
            type: 'account',
            disableRouteClick: true
          },
          {
            name: 'Email',
            title: 'Email',
            type: ''
          },
          {
            name: 'Phone',
            title: 'Phone',
            type: ''
          },
          {
            name: 'Partner',
            title: 'Partner',
            type: ''
          },
          {
            name: 'Score',
            title: 'Score',
            type: 'number'
          },
          {
            name: 'Comment',
            title: 'Comment',
            type: ''
          },
          {
            name: 'CustomerType',
            title: 'Customer Type',
            type: ''
          },
          {
            name: 'Date',
            title: 'Date',
            type: 'date'
          },
          {
            name: 'Time',
            title: 'Time',
            type: 'datetime'
          },
          {
            name: 'AgentId',
            title: 'Agent Id',
            type: ''
          },
          {
            name: 'Note',
            title: 'Note',
            type: ''
          },
          {
            name: 'Status',
            title: 'Status',
            type: ''
          },
          {
            type: 'nps-edit-column',
            disableRouteClick: true
          }
        ]
      }
    ],
    route: '/financial-reports/wordpress-cm/satisfaction',
    fixedPartnerId: 60
  },
  reportingWordPressCMToday: {
    charts: [
      {
        className: 'grid-col-20',
        type: CHART_TYPES.CARD,
        title: 'These stats are updated every hour.'
      },
      {
        title: 'Total Users Created',
        type: CHART_TYPES.CARD,
        name: 'wordpresscreativemailaccountscreatedtoday',
        className: 'grid-col-4',
        tooltip: 'The total number of Creative Mail users created today with a linked CTCT-Account.',
        sourcedatabase: null
      },
      {
        title: 'New Paid Plans',
        type: CHART_TYPES.CARD,
        name: 'todaysnewhostingunits',
        className: 'grid-col-4',
        tooltip: 'Todays number of newly bought plans',
        sourcedatabase: 'reportinggator'
      },
      {
        title: 'Join Conversion',
        type: CHART_TYPES.CARD,
        name: 'wordpresscreativemailtodaysjoinconversion',
        className: 'grid-col-4',
        suffix: '%',
        tooltip: 'Todays percentage of newly bought plans relative to newly created Creative Mail accounts with a linked CTCT-Account.'
      },
      {
        title: 'Cancels',
        type: CHART_TYPES.CARD,
        name: 'todayscancels',
        className: 'grid-col-4',
        tooltip: 'Todays number of cancelled plans',
        sourcedatabase: 'reportinggator'
      },
      {
        title: 'Total Revenue',
        type: CHART_TYPES.CARD,
        name: 'todaystotalrevenue',
        className: 'grid-col-4',
        prefix: '$',
        tooltip: 'Todays total revenue in $. Includes new Sales, Refunds, Renewals.',
        sourcedatabase: 'reportinggator'
      },
      {
        title: 'Account creation not finished',
        type: CHART_TYPES.CARD,
        name: 'wordpressaccountcreationnotfinishedtoday',
        className: 'grid-col-4',
        tooltip: 'All Creative Mail accounts where no CTCT-account was created',
        sourcedatabase: null
      },
      {
        title: 'Sales / Renewals / Refunds',
        type: CHART_TYPES.PIE,
        name: 'todaysSalesRenewalsRefunds',
        tooltipLabel: 'items',
        customFormat: CHART_TYPES.PIE_WITH_PRICE,
        className: 'grid-col-4',
        tooltip: 'Todays ratio of new sales, renewals and refunds revenue',
        sourcedatabase: 'reportinggator'
      },
      // {
      //   title: 'Term distribution',
      //   type: CHART_TYPES.PIE,
      //   name: 'todaystermdistribution',
      //   tooltipLabel: 'sales',
      //   className: 'grid-col-4',
      //   tooltip: 'Todays ratio of the different terms for all new sales (Hosting and Addons; no Refunds/Renewals)'
      // },
      // {
      //   title: 'Upsell / subscription',
      //   type: CHART_TYPES.PIE,
      //   name: 'todaysupsellsubscriptiondistribution',
      //   tooltipLabel: 'sales',
      //   className: 'grid-col-4',
      //   tooltip: 'Todays ratio of Upsells (addons) and Subscriptions (plans) for all new sales (no Refunds/Renewals)'
      // },
      // {
      //   title: 'Service distribution',
      //   type: CHART_TYPES.PIE,
      //   name: 'todaysplandistribution',
      //   tooltipLabel: 'sales',
      //   className: 'grid-col-4',
      //   tooltip: [
      //     'Todays ratio of the different types of plans for all new sales (Hosting and Addons; no Refunds/Renewals)'
      //   ]
      // },
      // {
      //   title: 'Currency',
      //   type: CHART_TYPES.PIE,
      //   name: 'todayscurrency',
      //   tooltipLabel: 'sales',
      //   className: 'grid-col-4',
      //   tooltip:
      //     'Todays ratio of the different currency types for all new sales (Hosting and Addons; no Refunds/Renewals)'
      // },
      {
        title: 'Todays transactions',
        type: CHART_TYPES.TABLE,
        name: 'todaystransactions',
        tooltip: [
          'Todays ratio of the different currency types for all new sales (no Refunds/Renewals)'
        ],
        className: 'grid-col-20',
        sorting: { default: { fieldNames: ['timestamp'], direction: false } },
        dontUseSourceDatabase: true,
        useBuilderPartnerFilter: true,
        start: moment()
          .subtract(1, 'd')
          .format('YYYY-MM-DD'),
        end: moment().format('YYYY-MM-DD'),
        sourcedatabase: 'reportinggator'
      }
    ],
    gridSize: 20,
    route: '/financial-reports/wordpress-cm/today',
    disableToday: true,
    hideDatePicker: true,
    fixedPartnerId: 60
  },
  usageWpCe: {
    charts: [
      {
        type: CHART_TYPES.GRAPH,
        name: 'WorkflowDataPerDay',
        title: 'Automations',
        tooltip: [
          'Users with Automation flows created: shows the number of users on a specific date that created an Automation for the first time.',
          'Users with Automation flows activated: shows the number of users on a specific date where an Automation was activated for the first time.',
          'Activated automation flows: shows the number of automations that were activated on a specific date.'
        ]
      },
      {
        type: CHART_TYPES.GRAPH,
        name: 'WooCommerceUsagePerDay',
        title: 'WooCommerce',
        description: [
          'Users with Woo Emails: shows the number of users that edited a Woo Email Template for the first time on a specific date. Please note that we can not show when a user first enabled a Woo Template as this information is stored at Woo only.',
          'Users with Woo Emails sent: shows the number of users where a Woo Email was sent for the first time on a specific date.']
      },
      {
        type: CHART_TYPES.STACKED,
        name: 'WooCommerceMailsSentPerDay',
        title: 'Amount of Woo Emails sent per type',
        tooltipLabel: 'emails',
        percentage: true,
        customDataHandler: (data, dates) => genericCustomDataHandler(data, dates, 'totalsent')
      },
      {
        type: CHART_TYPES.HEAT_TABLE,
        name: 'TimeToFirstPaidWordPress',
        title: 'Time to first paid',
        tooltipFormatterVerb: 'paid for',
        tooltipFormatterDirectObject: 'Creative Mail account'
      },
      {
        type: CHART_TYPES.STACKED,
        name: 'wordpresscreativemaildeactivationsurveyperday',
        title: 'Plugin uninstall reasons',
        tooltipLabel: '',
        customDataHandler: (data, dates) => genericCustomDataHandler(data, dates, 'count', 'value')
      },
      {
        type: CHART_TYPES.TABLE,
        name: 'LatestEmailAccounts',
        tooltip: `Displays the newest Creative Mail accounts. This excludes Creative Mail accounts that don't have a CTCT-account.`,
        title: 'Newest Accounts',
        limit: 500,
        sorting: { default: { fieldNames: ['TimeCreated'], direction: false } },
        headers: [
          {
            name: 'ReferenceAccountId',
            title: 'Account',
            type: 'account'
          },
          {
            name: 'EmailAddress',
            title: 'Email address',
            type: ''
          },
          {
            name: 'TimeCreated',
            title: 'Created on',
            type: 'datetime'
          },
          {
            name: 'Site',
            title: 'Site ID',
            type: 'number'
          },
          {
            name: 'CampaignsCreated',
            title: 'Campaigns created',
            type: 'number'
          },
          {
            name: 'CampaignsSend',
            title: 'Campaigns sent',
            type: 'number'
          },
          {
            name: 'CampaignsScheduled',
            title: 'Campaigns scheduled',
            type: 'number'
          },
          {
            name: 'TotalContacts',
            title: 'Subscribers',
            type: 'number'
          },
          {
            name: 'WooTemplatesEdited',
            title: 'Woo Templates edited',
            type: 'number'
          },
          {
            name: 'WooEmailsSent',
            title: 'Woo Emails sent',
            type: 'number'
          },
          {
            name: 'AutomationFlowsCreated',
            title: 'Automation Flows created',
            type: 'number'
          },
          {
            name: 'AutomationEmailsSent',
            title: 'Automation Emails sent',
            type: 'number'
          },
          {
            name: 'Status',
            title: 'Status',
            type: ''
          },
          {
            name: 'VerifiedOn',
            title: 'Verified on',
            type: 'datetime'
          },
          {
            name: 'SetupCompletedOn',
            title: 'Setup completed on',
            type: 'datetime'
          },
          {
            name: 'Brand',
            title: 'Brand',
            type: ''
          }
        ]
      },
      {
        type: CHART_TYPES.TABLE,
        name: 'latestemailaccountswithautomation',
        tooltip: `Displays the newest Creative Mail accounts with at least one automation flow. This excludes Creative Mail accounts that don't have a CTCT-account.`,
        title: 'Newest Accounts with Automations',
        limit: 500,
        sorting: { default: { fieldNames: ['TimeCreated'], direction: false } },
        headers: [
          {
            name: 'ReferenceAccountId',
            title: 'Account',
            type: 'account'
          },
          {
            name: 'EmailAddress',
            title: 'Email address',
            type: ''
          },
          {
            name: 'TimeCreated',
            title: 'Created on',
            type: 'datetime'
          },
          {
            name: 'Site',
            title: 'Site ID',
            type: 'number'
          },
          {
            name: 'CampaignsCreated',
            title: 'Campaigns created',
            type: 'number'
          },
          {
            name: 'CampaignsSend',
            title: 'Campaigns sent',
            type: 'number'
          },
          {
            name: 'CampaignsScheduled',
            title: 'Campaigns scheduled',
            type: 'number'
          },
          {
            name: 'TotalContacts',
            title: 'Subscribers',
            type: 'number'
          },
          {
            name: 'WooTemplatesEdited',
            title: 'Woo Templates edited',
            type: 'number'
          },
          {
            name: 'WooEmailsSent',
            title: 'Woo Emails sent',
            type: 'number'
          },
          {
            name: 'AutomationFlowsCreated',
            title: 'Automation Flows created',
            type: 'number'
          },
          {
            name: 'AutomationEmailsSent',
            title: 'Automation Emails sent',
            type: 'number'
          },
          {
            name: 'Status',
            title: 'Status',
            type: ''
          },
          {
            name: 'VerifiedOn',
            title: 'Verified on',
            type: 'datetime'
          },
          {
            name: 'SetupCompletedOn',
            title: 'Setup completed on',
            type: 'datetime'
          },
          {
            name: 'Brand',
            title: 'Brand',
            type: ''
          }
        ]
      },
      {
        type: CHART_TYPES.TABLE,
        name: 'latestemailaccountswithwoocommerce',
        tooltip: `Displays the newest Creative Mail accounts with at least one edited WooCommerce template. This excludes Creative Mail accounts that don't have a CTCT-account.`,
        title: 'Newest Accounts with WooCommerce',
        limit: 500,
        sorting: { default: { fieldNames: ['TimeCreated'], direction: false } },
        headers: [
          {
            name: 'ReferenceAccountId',
            title: 'Account',
            type: 'account'
          },
          {
            name: 'EmailAddress',
            title: 'Email address',
            type: ''
          },
          {
            name: 'TimeCreated',
            title: 'Created on',
            type: 'datetime'
          },
          {
            name: 'Site',
            title: 'Site ID',
            type: 'number'
          },
          {
            name: 'CampaignsCreated',
            title: 'Campaigns created',
            type: 'number'
          },
          {
            name: 'CampaignsSend',
            title: 'Campaigns sent',
            type: 'number'
          },
          {
            name: 'CampaignsScheduled',
            title: 'Campaigns scheduled',
            type: 'number'
          },
          {
            name: 'TotalContacts',
            title: 'Subscribers',
            type: 'number'
          },
          {
            name: 'WooTemplatesEdited',
            title: 'Woo Templates edited',
            type: 'number'
          },
          {
            name: 'WooEmailsSent',
            title: 'Woo Emails sent',
            type: 'number'
          },
          {
            name: 'AutomationFlowsCreated',
            title: 'Automation Flows created',
            type: 'number'
          },
          {
            name: 'AutomationEmailsSent',
            title: 'Automation Emails sent',
            type: 'number'
          },
          {
            name: 'Status',
            title: 'Status',
            type: ''
          },
          {
            name: 'VerifiedOn',
            title: 'Verified on',
            type: 'datetime'
          },
          {
            name: 'SetupCompletedOn',
            title: 'Setup completed on',
            type: 'datetime'
          },
          {
            name: 'Brand',
            title: 'Brand',
            type: ''
          }
        ]
      }
    ],
    route: '/financial-reports/wordpress-cm/usage',
    fixedPartnerId: 60
  },
  advancedWpCe: {
    charts: [
      {
        type: CHART_TYPES.CARD,
        title: 'Please note that charts on this page can only show data up to and including Yesterday.',
        className: 'grid-col-12'
      },
      {
        type: CHART_TYPES.GRAPH,
        name: 'WordpressCreativeMailJoinsPerSourcePerDay',
        title: 'Joins per Source',
        customDataHandler: (data, dates) => genericCustomDataHandler(data, dates, 'joins'),
        tooltip: () =>
          <>
            <Box mb={3}>
              Joins are all Creative Mail accounts that have a CTCT-account connected in the selected date period.
            </Box>
            <Box mb={3}>
              - wordpress-plugin-jetpackjitm<br />
              - wordpress-plugin-jetpacksetup-wizard<br />
              - wordpress-plugin-jetpackblock-editor<br />
              - wordpress-plugin-jetpackrecommendations<br />
              Are grouped under jetpack.
            </Box>
            <Box mb={3}>
              - wordpress-plugin-Bluehostpreinstalled-BHWB<br />
              - wordpress-plugin-Bluehostpreinstalled<br />
              - wordpress-plugin-unknown<br />
              Are grouped under bluehost.
            </Box>
          </>
      },
      {
        type: CHART_TYPES.GRAPH,
        name: 'WordpressCreativeMailPaidPerSourcePerDay',
        title: 'Paid per Source',
        customDataHandler: (data, dates) => genericCustomDataHandler(data, dates, 'paid'),
        tooltip: () =>
          <>
            <Box mb={3}>
              These are all paid plans for Creative Mail accounts purchased in the selected date period.
            </Box>
            <Box mb={3}>
              - wordpress-plugin-jetpackjitm<br />
              - wordpress-plugin-jetpacksetup-wizard<br />
              - wordpress-plugin-jetpackblock-editor<br />
              - wordpress-plugin-jetpackrecommendations<br />
              Are grouped under jetpack.
            </Box>
            <Box mb={3}>
              - wordpress-plugin-Bluehostpreinstalled-BHWB<br />
              - wordpress-plugin-Bluehostpreinstalled<br />
              - wordpress-plugin-unknown<br />
              Are grouped under bluehost.
            </Box>
          </>
      },
      {
        type: CHART_TYPES.GRAPH,
        name: 'WordpressCreativeMailConversionPerSourcePerDay',
        title: 'Conversion per Source',
        percentage: true,
        customDataHandler: (data, dates) => genericCustomDataHandler(data, dates, 'conversion'),
        tooltip: () =>
          <>
            <Box mb={3}>
              Signups are all Creative Mail accounts that have a CtCt-account connected. Conversion rates have a discrepancy because it’s possible that a customer joined on a date before the selected date period and bought a plan inside the selected date period.
            </Box>
            <Box mb={3}>
              - wordpress-plugin-jetpackjitm<br />
              - wordpress-plugin-jetpacksetup-wizard<br />
              - wordpress-plugin-jetpackblock-editor<br />
              - wordpress-plugin-jetpackrecommendations<br />
              Are grouped under jetpack.
            </Box>
            <Box mb={3}>
              - wordpress-plugin-Bluehostpreinstalled-BHWB<br />
              - wordpress-plugin-Bluehostpreinstalled<br />
              - wordpress-plugin-unknown<br />
              Are grouped under bluehost.
            </Box>
          </>
      },
      {
        type: CHART_TYPES.TABLE,
        name: 'wordpresscreativemailjoinspersource',
        tooltip: `Displays the number of joins (with a Constant Contact account) per traffic source.`,
        title: 'Joins/Sales per Source'
      },
      {
        title: 'Automattic Rev Share',
        type: CHART_TYPES.TABLE,
        name: 'WPCMTransactionsSalesRenewalsRefunds',
        tooltip: 'Shows the total Sales, Renewal and Refund $ for all transactions where the tracking source is wordpress or automattic. Totals are shown on Monthly basis. Make sure you select a full month in the Date Selector. Non $ Currencies are converted to $ Currency using the exchange rate of the transaction date.'
      }
    ],
    route: '/financial-reports/wordpress-cm/advanced',
    fixedPartnerId: 60,
    disableToday: true
  }
}
